import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import Overlay from '../Overlay';
import { useTier, usesPhoneLayout } from '../../lib/tiers';
import { generateSkuForCategory, nextAvailableSku } from '../../lib/skuGenerator';

// Assign missing SKUs -- the backfill for the catalog D&R already has.
//
// #471, Session 78s.3, gate decision S15 of
// docs/gates/Stede_SkuGeneration_Gate_471.html.
//
// WHAT THIS IS, in plain terms. Every item already in the catalog was created
// before Stede could write a code, so most of them have none. This proposes one
// for each, shows every single one, and writes nothing until somebody presses
// the button.
//
// WHY IT PROPOSES AND WAITS, rather than just doing it (gate callout H). Gate
// decision S8 freezes a SKU the moment it is assigned - it is never recomputed,
// because recomputing it would invalidate every printed label in the building.
// That makes this screen a ONE-WAY DOOR: after it runs, a wrong code is a wrong
// label forever. It is the only moment the codes can be corrected cheaply, so
// it is built to be read rather than to be fast.
//
// WHY EVERY ROW IS SHOWN AND NOTHING IS SUMMARISED (gate callout I). A screen
// saying "42 items will be updated" behind one button would be asking for
// consent to something nobody read. Forty-two rows is one sitting.
//
// DESKTOP ONLY, DELIBERATELY (gate callout J). This runs once, by one person,
// at a desk. Building a phone layout would be effort spent on a screen nobody
// opens twice. It is reached from the Catalog and is not in the sidebar for the
// same reason - a permanent nav item for a one-time task is clutter forever.

export default function AssignMissingSkusSheet({ onClose, onAssigned }) {
  const { tier } = useTier();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);        // { id, name, categoryLabel, code }
  // #471. Items that ALREADY have a SKU which is not what Stede would write.
  // Kept separate from `rows` because changing one is a different act from
  // filling a blank -- see the block above handleAssign().
  const [mismatched, setMismatched] = useState([]);  // { id, name, categoryLabel, current, proposed, adopt }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);  // { written, failures: [{name, message}] }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [{ data: cats, error: catErr }, { data: items, error: itemErr }] = await Promise.all([
          supabase.from('inventory_categories').select('id, name, parent_id, code'),
          supabase.from('inventory_items').select('id, name, sku, category_id').order('name'),
        ]);
        if (catErr) throw catErr;
        if (itemErr) throw itemErr;
        if (cancelled) return;

        const all = items || [];
        // A blank code is stored as NULL rather than as an empty string, which
        // is what lets the unique index tolerate many of them at once. Empty
        // strings are treated the same way here anyway - the distinction
        // matters to Postgres, not to the person reading this screen.
        const missing = all.filter(i => !i.sku || !String(i.sku).trim());

        // Codes already in use, so a proposal cannot land on one. Taken from
        // the WHOLE catalog rather than from the missing rows, because the
        // items that already have a code are exactly the ones to avoid.
        const taken = all
          .map(i => i.sku)
          .filter(s => s && String(s).trim())
          .map(s => String(s).trim());

        const catById = new Map((cats || []).map(c => [String(c.id), c]));
        const labelFor = (categoryId) => {
          const c = catById.get(String(categoryId));
          if (!c) return 'No category';
          const parent = c.parent_id ? catById.get(String(c.parent_id)) : null;
          return parent ? `${parent.name} > ${c.name}` : c.name;
        };

        // S10. Collisions are resolved as the list is built rather than at
        // save, and against the running set as well as against what is stored -
        // two items in THIS list can generate the same base code, and the
        // second one has to see the first one's allocation.
        const allocated = [...taken];
        const proposed = missing.map(item => {
          const base = generateSkuForCategory({
            categories: cats || [],
            categoryId: item.category_id,
            name: item.name,
          });
          const code = base ? nextAvailableSku(base, allocated) : '';
          if (code) allocated.push(code);
          return {
            id: item.id,
            name: item.name,
            categoryLabel: labelFor(item.category_id),
            code,
          };
        });

        // Items that already carry a code which is not the one Stede would
        // write. The gate's out-of-scope list is explicit that the hand-typed
        // SDT231648 found on production needs no special case BECAUSE "it is
        // covered by the backfill screen like any other row" -- and it is not
        // covered by a screen that only looks at blanks, which is what this
        // was until Colby caught it.
        //
        // Their proposal is computed against `allocated` too, so adopting one
        // cannot collide with a code just handed to a blank row.
        const withCode = all.filter(i => i.sku && String(i.sku).trim());
        const differing = withCode.map(item => {
          const base = generateSkuForCategory({
            categories: cats || [],
            categoryId: item.category_id,
            name: item.name,
          });
          if (!base) return null;
          const current = String(item.sku).trim();
          if (base.toUpperCase() === current.toUpperCase()) return null;
          return {
            id: item.id,
            name: item.name,
            categoryLabel: labelFor(item.category_id),
            current,
            proposed: nextAvailableSku(base, allocated.filter(c => c.toUpperCase() !== current.toUpperCase())),
            // Defaults OFF. Filling a blank takes nothing away; replacing a code
            // somebody typed does, and this screen has to stay safe on its
            // SECOND run, when the catalog may hold many deliberately-typed
            // codes rather than one hand-typed specimen.
            adopt: false,
          };
        }).filter(Boolean);

        setRows(proposed);
        setMismatched(differing);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load the catalog.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  function setCode(id, value) {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, code: value } : r)));
  }

  function toggleAdopt(id) {
    setMismatched(ms => ms.map(m => (m.id === id ? { ...m, adopt: !m.adopt } : m)));
  }

  // Checked live rather than at save. A duplicate discovered halfway through
  // writing would leave the catalog half-assigned, and the person would have to
  // work out which half - so the button is simply unavailable until the list is
  // internally consistent.
  const duplicates = useMemo(() => {
    const seen = new Map();
    const dupes = new Set();
    rows.forEach(r => {
      const key = (r.code || '').trim().toUpperCase();
      if (!key) return;
      if (seen.has(key)) { dupes.add(key); } else { seen.set(key, r.id); }
    });
    return dupes;
  }, [rows]);

  // The format the database will accept. Shown here rather than discovered as a
  // rejection after the write, which is the same reason the duplicate check is
  // live: this screen exists to be read before it acts.
  const badFormat = useMemo(
    () => new Set(rows.filter(r => r.code.trim() && !/^[A-Z0-9-]+$/.test(r.code.trim().toUpperCase()))
      .map(r => r.id)),
    [rows],
  );

  const assignable = rows.filter(r => r.code.trim());
  const adopting = mismatched.filter(m => m.adopt);
  const blocked = duplicates.size > 0 || badFormat.size > 0
    || (assignable.length === 0 && adopting.length === 0);

  async function handleAssign() {
    setSaving(true);
    setError('');
    const failures = [];
    let written = 0;

    // One row at a time, deliberately. A multi-row upsert would have to name
    // every NOT NULL column to avoid blanking one, which turns a SKU backfill
    // into a rewrite of the whole item - and a rewrite is not what anybody
    // pressed the button for.
    //
    // A partial run is recoverable BY DESIGN rather than by luck: this screen
    // reads the missing rows fresh every time it opens, so anything that failed
    // simply reappears next time with its code re-proposed. Nothing is lost and
    // nothing is written twice.
    // Replacements go FIRST. An adopted code frees the old value, and a blank
    // row could legitimately be proposed a code that the mismatched row is
    // about to give up -- doing the blanks first would hit the unique index.
    for (const m of adopting) {
      const { error: updErr } = await supabase
        .from('inventory_items')
        .update({ sku: m.proposed })
        .eq('id', m.id);
      if (updErr) {
        failures.push({ name: m.name, message: updErr.message });
      } else {
        written += 1;
      }
    }

    for (const row of assignable) {
      const { error: updErr } = await supabase
        .from('inventory_items')
        .update({ sku: row.code.trim() })
        .eq('id', row.id);
      if (updErr) {
        failures.push({ name: row.name, message: updErr.message });
      } else {
        written += 1;
      }
    }

    setSaving(false);
    setResult({ written, failures });
    if (written > 0) onAssigned?.(written);
  }

  const cell = { padding: '8px 10px', fontSize: 13, color: 'var(--black)', borderBottom: '1px solid #F0F0F0', verticalAlign: 'middle' };

  return (
    // #505, fixed at Session 79.15b under #417's D24. This was a CENTRED MODAL
    // ON A PHONE -- the third of #505's offenders and the one facing the other
    // way. D24 as corrected: sheet on Phone AND Tablet, modal on Desk and Wide.
    <Overlay
      variant={usesPhoneLayout(tier) ? 'sheet' : 'modal'}
      onClose={saving ? undefined : onClose}
      maxWidth={720}
    >
      <div style={{ padding: 24, maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
          Inventory &rsaquo; Catalog
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--black)', marginBottom: 10 }}>
          Assign missing SKUs
        </div>

        {loading && <div style={{ fontSize: 13, color: 'var(--gray-mid)' }}>Reading the catalog...</div>}

        {error && (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* The outcome, once it has run. Shown in place of the list rather than
            above it: the list is now out of date, and leaving it on screen
            would invite a second press. */}
        {result && (
          <div style={{ fontSize: 13, color: 'var(--black)' }}>
            <div style={{ background: '#E8F5EC', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <strong>{result.written}</strong> {result.written === 1 ? 'item now has a SKU' : 'items now have a SKU'}.
              {' '}These are frozen from now on and will not change if the item is recategorized.
            </div>
            {result.failures.length > 0 && (
              <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {result.failures.length} could not be saved and still {result.failures.length === 1 ? 'has' : 'have'} no SKU:
                </div>
                {result.failures.map((f, i) => (
                  <div key={i} style={{ marginTop: 2 }}>{f.name} &mdash; {f.message}</div>
                ))}
                <div style={{ marginTop: 6 }}>
                  Reopen this screen to try them again; nothing else was affected.
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !result && rows.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--gray-mid)' }}>
            Every item in the catalog already has a SKU. There is nothing to assign.
          </div>
        )}

        {!loading && !result && rows.length > 0 && (
          <>
            {/* The gate's own sentence, kept verbatim. It states the count, where
                the codes came from, and that nothing has happened yet - which is
                the whole contract of this screen in three clauses. */}
            <div style={{ fontSize: 13, color: 'var(--gray-mid)', marginBottom: 12 }}>
              {/* #705, 79.22b.4.6.1c -- the gate's sentence, with its noun and
                  verb chosen from the number: one item read "1 items have". */}
              <strong style={{ color: 'var(--black)' }}>{rows.length} {rows.length === 1 ? 'item has' : 'items have'} no SKU.</strong>{' '}
              Every code below was generated from the item&rsquo;s own category and name.
              Nothing is written until you confirm. Edit any of them now &mdash; after they are
              assigned they are frozen and will not change again, even if the item moves category.
            </div>

            {/* Found in the live test: the heading counts 44 and the button
                offers 42, and without this line nobody can tell why. A code
                cannot be built for an item filed under no category, because the
                category IS the first segment. Stating the number and the reason
                is cheaper than leaving somebody to work out whether two rows
                went missing. */}
            {rows.length > assignable.length && (
              <div style={{ fontSize: 12, color: 'var(--gray-mid)', marginBottom: 12, padding: '8px 10px', background: '#FFF8EC', borderRadius: 8 }}>
                <strong style={{ color: 'var(--black)' }}>
                  {rows.length - assignable.length} of these will be skipped
                </strong>{' '}
                because they are not filed under a category, and the category is what the
                code starts with. Give them a category on their own screen and they will
                appear here with a code. You can also type one in by hand below.
              </div>
            )}

            <div style={{ overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, flex: 1, minHeight: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Item', 'Category', 'SKU'].map(h => (
                      <th key={h} style={{ ...cell, textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', background: '#FAFAFA', position: 'sticky', top: 0 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const key = (r.code || '').trim().toUpperCase();
                    const isDupe = key && duplicates.has(key);
                    const isBad = badFormat.has(r.id);
                    const flagged = isDupe || isBad || !r.code.trim();
                    return (
                      <tr key={r.id}>
                        <td style={cell}>{r.name}</td>
                        <td style={{ ...cell, color: 'var(--gray-mid)', fontSize: 12 }}>{r.categoryLabel}</td>
                        <td style={cell}>
                          <input
                            type="text"
                            value={r.code}
                            onChange={e => setCode(r.id, e.target.value.toUpperCase())}
                            placeholder="No code &mdash; will be skipped"
                            style={{
                              width: '100%', height: 32, borderRadius: 6, padding: '0 8px',
                              // 16px minimum is a global rule for text entry (iOS
                              // auto-zoom), and this screen is desktop-only, but the
                              // rule is not conditional so it is honoured here too.
                              fontSize: 16,
                              border: `1px solid ${flagged ? '#E24B4A' : 'var(--border)'}`,
                              color: 'var(--black)',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            }}
                          />
                          {isDupe && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>Same as another row</div>}
                          {isBad && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>Letters, numbers and hyphens only</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {mismatched.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
                  {mismatched.length === 1
                    ? 'One item already has a SKU in a different format'
                    : `${mismatched.length} items already have a SKU in a different format`}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-mid)', marginBottom: 8 }}>
                  These were typed in by hand before Stede could write codes. Nothing happens
                  to them unless you tick one. Replacing a code is permanent in the same way
                  assigning one is &mdash; if a label has already been printed with the old
                  code on it, that label will be wrong.
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {mismatched.map(m => (
                        <tr key={m.id}>
                          <td style={{ ...cell, width: 34 }}>
                            <input type="checkbox" checked={m.adopt} onChange={() => toggleAdopt(m.id)}
                              aria-label={`Replace the SKU on ${m.name}`}
                              style={{ width: 16, height: 16, cursor: 'pointer' }} />
                          </td>
                          <td style={cell}>
                            {m.name}
                            <div style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{m.categoryLabel}</div>
                          </td>
                          <td style={{ ...cell, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>
                            <span style={{ color: 'var(--gray-mid)', textDecoration: m.adopt ? 'line-through' : 'none' }}>
                              {m.current}
                            </span>
                            <span style={{ color: 'var(--gray-mid)' }}> &rarr; </span>
                            <span style={{ color: m.adopt ? 'var(--black)' : 'var(--gray-mid)', fontWeight: m.adopt ? 700 : 400 }}>
                              {m.proposed}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Says WHY the button is unavailable. A disabled control with no
                reason beside it is the same as a broken one. */}
            {blocked && (
              <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 10 }}>
                {duplicates.size > 0 && <div>Two or more rows share a code. Every SKU has to be different.</div>}
                {badFormat.size > 0 && <div>A code contains something other than letters, numbers and hyphens.</div>}
                {assignable.length === 0 && adopting.length === 0 && <div>Nothing is selected to assign.</div>}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {/* The app's own button classes, not hand-rolled styles. The first
              version of this footer styled the confirm button inline with
              `background: var(--inventory)` -- a variable that DOES NOT EXIST.
              It resolved to transparent, and with white text on a white modal
              the button rendered at its full 99x38 and was completely
              invisible. Colby found it by asking where the submit button was.
              A domain colour that only lives in CLAUDE.md is not a token; the
              app's palette is --green, and .btn-primary already carries it. */}
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && rows.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleAssign}
              disabled={saving || blocked}
            >
              {saving
                ? 'Assigning...'
                : `Assign ${assignable.length + adopting.length} SKU${(assignable.length + adopting.length) === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}
